import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, ShieldAlert } from "lucide-react";
import { getPreopenStatus } from "../api/preopen";
import { formatIst } from "../lib/format";

/** Pre-open readiness, on the dashboard.
 *
 *  Green is deliberately quiet — a success notice that shouts as loudly as a
 *  failure retrains you to skim both, and then the one morning it matters gets
 *  skimmed too. Red carries the deployment, the failing channel and the
 *  remedy, because at 09:12 nobody goes looking for documentation.
 */
export default function PreopenCard() {
  const { data } = useQuery({
    queryKey: ["preopen-status"],
    queryFn: getPreopenStatus,
    // Cheap (it reads the last result, it does not re-probe), so a short
    // interval is fine and the card updates the moment a check lands.
    refetchInterval: 30_000,
  });

  if (!data) return null;

  const schedule = data.schedule?.join(" and ") ?? "";

  if (!data.checked) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-border bg-surface px-4 py-3 text-sm">
        <Clock size={16} className="mt-0.5 shrink-0 text-muted" />
        <div>
          <div className="font-medium text-fg">Pre-open check not run yet</div>
          <div className="text-xs text-muted mt-0.5">
            Runs at {schedule} IST on trading days, before the {data.market_open}{" "}
            open.
          </div>
        </div>
      </div>
    );
  }

  const failing = (data.deployments ?? []).filter((d) => !d.ok);

  if (data.ok) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-up-200 dark:border-up-400/30 bg-up-50 dark:bg-up-500/10 px-4 py-3 text-sm">
        <CheckCircle2
          size={16}
          className="mt-0.5 shrink-0 text-up-600 dark:text-up-400"
        />
        <div>
          <div className="font-medium text-up-700 dark:text-up-400">
            Ready to trade
          </div>
          <div className="text-xs text-muted mt-0.5">
            Broker tokens, option chain and price feed all checked
            {data.checked_at ? ` at ${formatIst(data.checked_at)}` : ""}. Nothing
            to do.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-down-200 dark:border-down-400/30 bg-down-50 dark:bg-down-500/10 px-4 py-3 text-sm">
      <div className="flex items-start gap-2.5">
        <ShieldAlert
          size={16}
          className="mt-0.5 shrink-0 text-down-600 dark:text-down-400"
        />
        <div className="min-w-0">
          <div className="font-medium text-down-700 dark:text-down-400">
            Not ready to trade — market opens {data.market_open}
          </div>
          <ul className="mt-1.5 space-y-1">
            {failing.map((d) => (
              <li key={`${d.deployment}-${d.connection}`} className="text-xs">
                <span className="font-medium text-fg">{d.deployment}</span>{" "}
                <span className="text-muted">({d.connection})</span>
                <ul className="mt-0.5 ml-3 list-disc text-muted">
                  {d.problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <div className="text-xs text-muted mt-2">
            Kite access tokens expire daily and can only be renewed by logging
            in through the browser — open the Brokers page and use Refresh on
            the affected connection.
          </div>
        </div>
      </div>
    </div>
  );
}
